import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppContextService } from '@partou/core';
import { catchError, forkJoin, map, of, type Observable } from 'rxjs';
import type {
  ApiChild,
  ApiChildrenResponse,
  ApiChildResults,
  ApiNote,
  ApiNotesResponse,
  ApiQuestionnaireListItem,
  ApiQuestionnairesResponse,
  Child,
  ChildObservation,
  ChildrenQuery,
  ChildrenResult,
  ParentReportRequest,
} from '../types';
import { type ApiQuestionnaireStatus, type ChildSortField, NON_DELETED_QUESTIONNAIRE_STATUSES } from '../constants';
import { toChildDetail } from '../mappers/child-detail.api';
import { toChildListItem } from '../mappers/child-list-item.api';
import { toChildObservation } from '../mappers/child-observation.api';
import { toApiGenerateParentReportRequest } from '../mappers/parent-report.api';

const CHILDREN_ENDPOINT: Readonly<string> = '/children';
const QUESTIONNAIRES_ENDPOINT: Readonly<string> = '/questionnaires';
const API_SORT_FIELDS: Readonly<Record<ChildSortField, string>> = {
  name: 'firstName',
  group: 'group',
};

const EMPTY_RESULTS: ApiChildResults = { questionnaires: [] };
const NO_NOTES: ReadonlyArray<ApiNote> = [];
const NO_OBSERVATIONS: ReadonlyArray<ApiQuestionnaireListItem> = [];

@Injectable()
export class ChildrenApiService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly context: AppContextService = inject(AppContextService);

  getChildren(query: ChildrenQuery): Observable<ChildrenResult> {
    let params: HttpParams = new HttpParams().set('limit', query.pageSize).set('offset', query.page * query.pageSize);
    if (query.locationId !== '') params = params.set('locationId', query.locationId);
    if (query.groupId !== '') params = params.set('groupId', query.groupId);
    if (query.hidden !== null) params = params.set('hidden', query.hidden);
    if (query.search !== '') params = params.set('nameSearch', query.search);
    if (query.sortField !== null) {
      params = params
        .set('sortField', API_SORT_FIELDS[query.sortField])
        .set('sortDirection', query.sortDirection === 'desc' ? 'DESC' : 'ASC');
    }
    const now: number = Date.now();
    return this.http.get<ApiChildrenResponse>(this.context.buildEndpoint(CHILDREN_ENDPOINT), { params }).pipe(
      map(
        (response: ApiChildrenResponse): ChildrenResult => ({
          items: response.items.map((api: ApiChild): Child => toChildListItem(api, now)),
          total: response.pagination.total,
        }),
      ),
    );
  }

  getChild(id: string): Observable<Child> {
    const childPath = `${CHILDREN_ENDPOINT}/${id}`;
    // The backend parses repeated array params under a bracketed key (statuses[]), not the bare name.
    const observationParams: HttpParams = NON_DELETED_QUESTIONNAIRE_STATUSES.reduce(
      (params: HttpParams, status: ApiQuestionnaireStatus): HttpParams => params.append('statuses[]', status),
      new HttpParams().set('childId', id),
    );
    return forkJoin({
      base: this.http.get<ApiChild>(this.context.buildEndpoint(childPath)),
      results: this.http
        .get<ApiChildResults>(this.context.buildEndpoint(`${childPath}/results`))
        .pipe(catchError((): Observable<ApiChildResults> => of(EMPTY_RESULTS))),
      notes: this.http
        .get<ApiNotesResponse>(this.context.buildEndpoint(`${childPath}/notes`))
        .pipe(catchError((): Observable<ApiNotesResponse> => of({ items: NO_NOTES }))),
      observations: this.http
        .get<ApiQuestionnairesResponse>(this.context.buildEndpoint(QUESTIONNAIRES_ENDPOINT), {
          params: observationParams,
        })
        .pipe(catchError((): Observable<ApiQuestionnairesResponse> => of({ items: NO_OBSERVATIONS }))),
    }).pipe(
      map(
        (sources: {
          base: ApiChild;
          results: ApiChildResults;
          notes: ApiNotesResponse;
          observations: ApiQuestionnairesResponse;
        }): Child => toChildDetail(sources.base, sources.results, sources.notes.items, sources.observations.items),
      ),
    );
  }

  deleteNote(childId: string, noteId: string): Observable<void> {
    return this.http.delete<void>(this.context.buildEndpoint(`${CHILDREN_ENDPOINT}/${childId}/notes/${noteId}`));
  }

  deleteObservation(_childId: string, observationId: string): Observable<void> {
    return this.http.delete<void>(this.context.buildEndpoint(`${QUESTIONNAIRES_ENDPOINT}/${observationId}`));
  }

  createObservation(childId: string, locationId: string): Observable<ChildObservation> {
    return this.http
      .put<ApiQuestionnaireListItem>(this.context.buildEndpoint(`${QUESTIONNAIRES_ENDPOINT}/create`), {
        childId,
        locationId,
      })
      .pipe(map(toChildObservation));
  }

  updateChildHidden(childId: string, hidden: boolean): Observable<Child> {
    return this.http
      .put<ApiChild>(this.context.buildEndpoint(`${CHILDREN_ENDPOINT}/${childId}`), { hidden })
      .pipe(map((api: ApiChild): Child => toChildListItem(api)));
  }

  generateParentReport(childId: string, request: ParentReportRequest): Observable<Blob> {
    return this.http.post(
      this.context.buildEndpoint(`${CHILDREN_ENDPOINT}/${childId}/parent-report`),
      toApiGenerateParentReportRequest(request),
      { responseType: 'blob' },
    );
  }
}
